import { IconCircleCheck, IconMailOpened } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { isRequestError } from "api-def";
import { useEffect, useState } from "react";
import type { InvitationPreview, ProjectRole } from "shared/ProjectAccess";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import {
  acceptOrganisationInvite,
  acceptProjectInvite,
  previewOrganisationInvite,
  previewProjectInvite,
} from "src/CalmLensApi";
import Logo from "src/components/app/Logo/Logo";
import SignOutButton from "src/components/app/SignOutButton/SignOutButton";
import Button from "src/components/common/Button/Button";
import Callout from "src/components/common/Callout/Callout";
import Card from "src/components/common/Card/Card";
import IconTile from "src/components/common/IconTile/IconTile";
import Link from "src/components/common/Link/Link";
import RelativeTime from "src/components/common/RelativeTime/RelativeTime";
import Typography from "src/components/common/Typography/Typography";
import { useAuth } from "src/lib/contexts/AuthContext";
import { setCookie } from "src/lib/hooks/ClientHooks";
import { organisationPath, projectPath } from "src/lib/PortalPaths";
import { projectTeamErrorMessage } from "src/lib/ProjectTeamErrors";
import { useExtracted, useTranslateText } from "#i18n";

// Project and organisation invitations: the token arrives in the URL fragment (never sent to the
// server), as `token` for a project and `organisation` for an organisation. Signed-out visitors get
// the sign-in page here first (the `_authed` layout).
export const Route = createFileRoute("/_authed/invite")({
  component: InvitePage,
});

function InvitePage() {
  const t = useExtracted();
  const translateText = useTranslateText();
  const { authState } = useAuth();
  const [token, setToken] = useState("");
  const [kind, setKind] = useState<"project" | "organisation">("project");
  const [preview, setPreview] = useState<InvitationPreview>();
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The joined project's or organisation's portal page, linked directly so it doesn't depend on
  // the optional selected-project cookie.
  const [accepted, setAccepted] = useState<string>();
  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const organisation = fragment.get("organisation");
    setKind(organisation ? "organisation" : "project");
    setToken(organisation ?? fragment.get("token") ?? "");
  }, []);
  useEffect(() => {
    if (!token || accepted) return;
    let current = true;
    (kind === "organisation" ? previewOrganisationInvite : previewProjectInvite)
      .submit({ body: { token } })
      .then(({ data }) => current && setPreview(data))
      .catch(
        (error) =>
          current &&
          setPreviewError(
            projectTeamErrorMessage(
              error,
              {
                fallback: t("This invitation has expired or is no longer valid."),
              },
              translateText,
            ),
          ),
      );
    return () => {
      current = false;
    };
  }, [token, kind, accepted, t]);

  const roleLabels: Record<ProjectRole, string> = {
    owner: t("an owner"),
    admin: t("an administrator"),
    editor: t("an editor"),
    viewer: t("a viewer"),
    reviewer: t("a human reviewer"),
    custom: t("a member with custom access"),
  };
  const roleDescriptions: Record<ProjectRole, string> = {
    owner: t("Owners manage the organisation, its billing and every project in it."),
    admin: t("Administrators manage everything in the project except billing."),
    editor: t("Editors upload and manage assets and folders."),
    viewer: t("Viewers see and download assets."),
    reviewer: t(
      "Reviewers check a sample of automated moderation decisions in the human review queue. They don’t see the project’s other assets, settings or keys.",
    ),
    custom: t("Custom access is limited to the folders and permissions chosen for you."),
  };

  async function accept() {
    setBusy(true);
    setError("");
    try {
      if (kind === "organisation") {
        const { data } = await acceptOrganisationInvite.submit({
          body: { token },
        });
        // Open the portal in the organisation just joined.
        setCookie(FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.name, data.orgId, {
          expiresMs: FIRST_PARTY_COOKIES.SELECTED_ORGANISATION_ID.expirationMs,
        });
        setCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name, null);
        setAccepted(organisationPath(data.orgId));
      } else {
        const { data } = await acceptProjectInvite.submit({ body: { token } });
        setCookie(FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.name, data.projectId, {
          expiresMs: FIRST_PARTY_COOKIES.SELECTED_PROJECT_ID.expirationMs,
        });
        setAccepted(projectPath(data.projectId));
      }
      window.history.replaceState(null, "", "/invite");
      setToken("");
    } catch (error) {
      setError(
        projectTeamErrorMessage(
          error,
          {
            fallback: t("Unable to accept invitation."),
          },
          translateText,
        ),
      );
      if (kind === "organisation") {
        const body = isRequestError(error) ? error.response?.data : undefined;
        if (body?.code === "organisation/team-error" && typeof body.message === "string")
          setError(body.message);
      }
    } finally {
      setBusy(false);
    }
  }

  const email = authState?.user.email ?? "";
  const differentAccount = (
    <SignOutButton
      label={t("Use a different account")}
      redirectTo={`/invite#${kind === "organisation" ? "organisation" : "token"}=${encodeURIComponent(token)}`}
    />
  );
  const title = accepted
    ? t("You’re in")
    : preview
      ? t("Join {name}", { name: preview.name })
      : kind === "organisation"
        ? t("Organisation invitation")
        : t("Project invitation");
  const description = accepted
    ? kind === "organisation"
      ? t("You’re now an owner of {name}.", { name: preview?.name ?? "" })
      : t("You’ve joined {name}.", { name: preview?.name ?? "" })
    : preview
      ? preview.invitedBy
        ? t("{inviter} invited you to join as {role}.", {
            inviter: preview.invitedBy,
            role: roleLabels[preview.role],
          })
        : t("You’re invited to join as {role}.", {
            role: roleLabels[preview.role],
          })
      : t("Open the invitation link from your email to continue.");

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-lg flex-col items-center justify-center gap-8 px-4 py-16">
      <Link href="/" aria-label={t("CalmLens home")}>
        <Logo size="lg" />
      </Link>
      <Card className="w-full">
        <header
          className="flex flex-col items-center gap-3 text-center"
          role={accepted ? "status" : undefined}
        >
          <IconTile size="lg" shape="circle" fill="soft" color={accepted ? "success" : "primary"}>
            {accepted ? <IconCircleCheck /> : <IconMailOpened />}
          </IconTile>
          <Typography as="h1" variant="heading2">
            {title}
          </Typography>
          <Typography variant="bodySmall" tone="muted">
            {description}
          </Typography>
        </header>
        <div className="space-y-4">
          {accepted ? null : previewError ? (
            <Callout role="alert" color="danger" size="small">
              {previewError}
            </Callout>
          ) : preview ? (
            <>
              <Typography variant="bodySmall" tone="muted">
                {roleDescriptions[preview.role]}
              </Typography>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted">{t("Signed in as")}</dt>
                <dd className="min-w-0 break-words">{email}</dd>
                <dt className="text-muted">{t("Expires")}</dt>
                <dd>
                  <RelativeTime date={preview.expiresAt} />
                </dd>
              </dl>
              {!preview.sentToSignedInEmail && (
                <Callout role="alert" color="warning" size="small">
                  {t(
                    "This invitation was sent to a different email address. Sign in with that address to accept it.",
                  )}
                </Callout>
              )}
            </>
          ) : null}
          {error && (
            <Callout role="alert" color="danger" size="small">
              {error}
            </Callout>
          )}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            {accepted ? (
              <Button
                href={accepted}
                label={
                  kind === "organisation"
                    ? t("Open portal")
                    : preview?.role === "reviewer"
                      ? t("Open review queue")
                      : t("Open project")
                }
              />
            ) : (
              <>
                {differentAccount}
                {(!preview || preview.sentToSignedInEmail) && !previewError && (
                  <Button
                    label={t("Accept invitation")}
                    disabled={!token || !preview || busy}
                    loading={busy}
                    onClick={() => void accept()}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </main>
  );
}
